import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useCart } from '../CartContext/CartContext';
import './styles/phoneMenu.css';

const PhoneMenu = () => {
  const { getCartItemCount } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [omItems, setOmItems] = useState([]);
  const [terapiItems, setTerapiItems] = useState([]);
  const [uddannelseItems, setUddannelseItems] = useState([]);
  const menuRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    // Luk menuen når URL'en ændres
    setIsOpen(false);
    setExpandedSection(null);
  }, [location.pathname]);

  useEffect(() => {
    // Toggle body scroll når menuen er åben
    if (isOpen) {
      document.body.classList.add('phone-menu-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('phone-menu-open');
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  useEffect(() => {
    // Klik uden for menuen lukker den
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && !event.target.closest('.phone-menu-toggle') && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const fetchMenuItems = async () => {
      try {
        // Hent psykologer til "Om os" undermenuen
        const omDocRef = doc(db, 'content', 'om');
        const omDoc = await getDoc(omDocRef);
        
        if (omDoc.exists() && omDoc.data().psykologer) {
          setOmItems(omDoc.data().psykologer.map(p => ({
            id: p.id,
            title: p.navn,
            link: `/om/${p.id}`
          })));
        }
        
        // Hent terapiområder til "Terapi" undermenuen
        const terapiOmraderDocRef = doc(db, 'content', 'terapiOmrader');
        const terapiOmraderDoc = await getDoc(terapiOmraderDocRef);
        
        if (terapiOmraderDoc.exists() && terapiOmraderDoc.data().omrader) {
          setTerapiItems(terapiOmraderDoc.data().omrader.map(o => ({
            id: o.id,
            title: o.title,
            link: `/terapi/${o.id}`
          })));
        }
        
        // Hent uddannelser til "Uddannelse" undermenuen
        const uddannelserDocRef = doc(db, 'content', 'uddannelser');
        const uddannelserDoc = await getDoc(uddannelserDocRef);
        
        if (uddannelserDoc.exists() && uddannelserDoc.data().uddannelser) {
          setUddannelseItems(uddannelserDoc.data().uddannelser.map(u => ({
            id: u.id,
            title: u.title,
            link: `/uddannelse/${u.id}`
          })));
        }
      } catch (error) {
        console.error('Fejl ved hentning af menu-data:', error);
      }
    };

    fetchMenuItems();
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    if (isOpen) {
      setExpandedSection(null);
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="phone-menu-container">
      <button 
        className={`phone-menu-toggle ${isOpen ? 'active' : ''}`} 
        onClick={toggleMenu}
        aria-label="Åbn menu"
        aria-expanded={isOpen}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <div className={`phone-menu-overlay ${isOpen ? 'active' : ''}`} onClick={toggleMenu}></div>
      
      <nav ref={menuRef} className={`phone-menu ${isOpen ? 'active' : ''}`}>
        <div className="phone-menu-header">
          <div className="phone-menu-logo">
            <Link to="/" onClick={() => setIsOpen(false)}>
              <img src="/media/logo/psykologiskperspektiv-logo.avif" alt="Psykologisk Perspektiv" />
            </Link>
          </div>
          <button 
            className="phone-menu-close" 
            onClick={toggleMenu}
            aria-label="Luk menu"
          >
            &times;
          </button>
        </div>
        
        <ul className="phone-menu-items">
          <li>
            <Link to="/" className="phone-menu-link">Forside</Link>
          </li>
          
          <li className={`phone-menu-dropdown ${expandedSection === 'om' ? 'active' : ''}`}>
            <div className="phone-menu-dropdown-header" onClick={() => toggleSection('om')}>
              <Link to="/om" className="phone-menu-link" onClick={(e) => e.stopPropagation()}>Om os</Link>
              <button className="phone-menu-expand" aria-label="Vis undermenu">
                <span className="phone-menu-arrow">▼</span>
              </button>
            </div>
            <ul className="phone-menu-submenu">
              {omItems.map(item => (
                <li key={item.id}>
                  <Link to={item.link} className="phone-menu-sublink">{item.title}</Link>
                </li>
              ))}
            </ul>
          </li>
          
          <li className={`phone-menu-dropdown ${expandedSection === 'terapi' ? 'active' : ''}`}>
            <div className="phone-menu-dropdown-header" onClick={() => toggleSection('terapi')}>
              <Link to="/terapi" className="phone-menu-link" onClick={(e) => e.stopPropagation()}>Terapi</Link>
              <button className="phone-menu-expand" aria-label="Vis undermenu">
                <span className="phone-menu-arrow">▼</span>
              </button>
            </div>
            <ul className="phone-menu-submenu">
              {terapiItems.map(item => (
                <li key={item.id}>
                  <Link to={item.link} className="phone-menu-sublink">{item.title}</Link>
                </li>
              ))}
            </ul>
          </li>
          
          <li>
            <Link to="/supervision" className="phone-menu-link">Supervision</Link>
          </li>
          
          <li className={`phone-menu-dropdown ${expandedSection === 'uddannelse' ? 'active' : ''}`}>
            <div className="phone-menu-dropdown-header" onClick={() => toggleSection('uddannelse')}>
              <Link to="/uddannelse" className="phone-menu-link" onClick={(e) => e.stopPropagation()}>Uddannelse</Link>
              <button className="phone-menu-expand" aria-label="Vis undermenu">
                <span className="phone-menu-arrow">▼</span>
              </button>
            </div>
            <ul className="phone-menu-submenu">
              {uddannelseItems.map(item => (
                <li key={item.id}>
                  <Link to={item.link} className="phone-menu-sublink">{item.title}</Link>
                </li>
              ))}
            </ul>
          </li>
          
          <li>
            <Link to="/praktisk-priser" className="phone-menu-link">Praktisk & Priser</Link>
          </li>
          
          <li>
            <Link to="/shop" className="phone-menu-link">Shop</Link>
          </li>
          
          <li>
            <Link to="/viden" className="phone-menu-link">Viden</Link>
          </li>
          
          <li>
            <Link to="/kontakt" className="phone-menu-link">Kontakt</Link>
          </li>
          
          <li>
            <Link to="/kurv" className="phone-menu-link">
              Kurv {getCartItemCount() > 0 && <span className="phone-cart-count">({getCartItemCount()})</span>}
            </Link>
          </li>
        </ul>
      </nav>
    </div>
  );
};

export default PhoneMenu;
